import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';

const ZOOM_SCALE_TOLERANCE = 0.01;
const RESIZE_SETTLE_MS = 250;
const KEYBOARD_VISIBLE_RATIO = 0.8;

/**
 * Locks the app shell to the exact visible webview height via --app-height.
 * CSS viewport units (vh/dvh) over-report on iPad Safari (floating toolbar
 * overlap), so the real visible height is measured from visualViewport instead.
 */
@Injectable({ providedIn: 'root' })
export class ViewportHeightService {
  private settleTimer?: ReturnType<typeof setTimeout>;
  private lastInnerHeight = 0;
  private shouldSkip: () => boolean = () => false;
  private readonly realResizeSource = new Subject<void>();

  /**
   * Emits once per genuine chrome resize (toolbar collapse, tab-switch,
   * orientation change) right after --app-height is updated — never for a
   * keyboard-only visualViewport shrink. Components with their own
   * JS-computed (not CSS-relative) height, e.g. a grid's per-row pixel
   * height, should subscribe here to recompute in step with --app-height.
   */
  readonly realResize$ = this.realResizeSource.asObservable();

  private readonly apply = (): void => {
    if (this.shouldSkip()) return;
    const vv = window.visualViewport;
    // Never write a scaled (zoomed) height — visualViewport.height shrinks while
    // zoomed, which would leave a gap. Re-fit cleanly once the user returns to 1x.
    if (vv && Math.abs(vv.scale - 1) > ZOOM_SCALE_TOLERANCE) return;

    // The on-screen keyboard shrinks visualViewport.height while leaving
    // window.innerHeight (the layout viewport) untouched on iOS Safari — that
    // signature is what tells it apart from a real chrome resize (toolbar
    // collapse, tab-switch, orientation change), where innerHeight moves too.
    // Only a real chrome resize should update --app-height; a keyboard-only
    // shrink must leave the app shell (and any open dialog) alone.
    const innerHeightChanged = window.innerHeight !== this.lastInnerHeight;
    const keyboardLikelyOpen = !!vv && vv.height < window.innerHeight * KEYBOARD_VISIBLE_RATIO;
    if (!innerHeightChanged && keyboardLikelyOpen) return;

    this.lastInnerHeight = window.innerHeight;
    const height = vv ? vv.height : window.innerHeight;
    document.documentElement.style.setProperty('--app-height', `${Math.round(height)}px`);
    // Toolbar changes can leave the visual viewport panned (gap at top) — clear it.
    if (window.scrollY !== 0 || window.scrollX !== 0) {
      window.scrollTo(0, 0);
    }
    this.realResizeSource.next();
  };

  // Apply immediately, then again after the toolbar/zoom animation settles so we
  // capture the final resting height (the multiple->single tab transition animates).
  private readonly scheduleApply = (): void => {
    this.apply();
    clearTimeout(this.settleTimer);
    this.settleTimer = setTimeout(this.apply, RESIZE_SETTLE_MS);
  };

  /**
   * Starts tracking visualViewport changes. `shouldSkip` is re-evaluated on
   * every event, so callers can gate by current route without re-subscribing.
   */
  enable(shouldSkip: () => boolean = () => false): void {
    this.shouldSkip = shouldSkip;
    this.lastInnerHeight = window.innerHeight;
    this.apply();
    window.visualViewport?.addEventListener('resize', this.scheduleApply);
    window.visualViewport?.addEventListener('scroll', this.scheduleApply);
    window.addEventListener('orientationchange', this.scheduleApply);
    window.addEventListener('resize', this.scheduleApply);
  }

  disable(): void {
    clearTimeout(this.settleTimer);
    window.visualViewport?.removeEventListener('resize', this.scheduleApply);
    window.visualViewport?.removeEventListener('scroll', this.scheduleApply);
    window.removeEventListener('orientationchange', this.scheduleApply);
    window.removeEventListener('resize', this.scheduleApply);
  }
}
